/* Cart line selection.

   Shopify checks out the entire cart, so buying a subset means the unselected
   lines have to leave the cart first. They are stashed locally and offered
   back on the next visit rather than silently destroyed, which is what makes
   this safe to use as a "save some for later" gesture instead of a trap. */
(() => {
  const KEY = 'todo:saved-for-later';
  const page = document.querySelector('.cart-page');
  if (!page) return;

  const picks = () => [...page.querySelectorAll('[data-cart-pick]')];
  const all = page.querySelector('[data-cart-pick-all]');
  const countEl = page.querySelector('[data-cart-pick-count]');
  const subtotalEl = page.querySelector('[data-cart-subtotal]');
  const totalEl = page.querySelector('[data-cart-total]');
  const checkoutBtn = page.querySelector('[name="checkout"], .cart-summary__button');

  let cart = null;
  const money = c => '$' + (c / 100).toFixed(2);

  function selectedKeys() {
    return picks().filter(p => p.checked).map(p => p.value);
  }

  function paint() {
    if (!cart) return;
    const keys = new Set(selectedKeys());
    let cents = 0;
    let units = 0;
    (cart.items || []).forEach(i => {
      if (!keys.has(i.key)) return;
      cents += i.final_line_price;
      units += i.quantity;
    });

    if (subtotalEl) subtotalEl.textContent = money(cents);
    if (totalEl) totalEl.textContent = money(cents);
    if (countEl) {
      const n = keys.size;
      countEl.textContent = n === picks().length
        ? units + (units === 1 ? ' item' : ' items')
        : n + ' of ' + picks().length + ' selected';
    }
    if (all) {
      all.checked = keys.size === picks().length;
      all.indeterminate = keys.size > 0 && keys.size < picks().length;
    }
    page.querySelectorAll('[data-cart-item]').forEach(row => {
      row.classList.toggle('is-deselected', !keys.has(row.dataset.key));
    });
    if (checkoutBtn) {
      checkoutBtn.disabled = keys.size === 0;
      checkoutBtn.textContent = keys.size === 0
        ? 'Select something to check out'
        : (keys.size < picks().length ? 'Checkout ' + keys.size + ' selected' : 'Checkout');
    }
  }

  function load() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(r => r.json()).then(c => {
        cart = c;
        // Line keys are regenerated on every cart change, so the checkbox
        // values have to follow their row or selection silently detaches.
        page.querySelectorAll('[data-cart-item]').forEach(row => {
          const box = row.querySelector('[data-cart-pick]');
          if (box && box.value !== row.dataset.key) box.value = row.dataset.key;
        });
        paint();
      });
  }

  page.addEventListener('change', (e) => {
    if (e.target.matches('[data-cart-pick]')) paint();
    if (e.target === all) {
      picks().forEach(p => { p.checked = all.checked; });
      paint();
    }
  });

  // Quantity edits change line totals, so recompute from the server.
  page.addEventListener('click', (e) => {
    if (e.target.closest('[data-cart-qty-inc], [data-cart-qty-dec], [data-cart-item-remove]')) {
      setTimeout(load, 450);
    }
  });

  if (checkoutBtn) {
    checkoutBtn.addEventListener('click', (e) => {
      const keys = new Set(selectedKeys());
      if (!keys.size) { e.preventDefault(); return; }
      const drop = (cart.items || []).filter(i => !keys.has(i.key));
      if (!drop.length) return;           // everything selected, normal checkout

      e.preventDefault();
      // Remember what is being set aside so it can be restored.
      try {
        const saved = JSON.parse(localStorage.getItem(KEY) || '[]');
        drop.forEach(i => saved.push({ id: i.variant_id, quantity: i.quantity, title: i.product_title }));
        localStorage.setItem(KEY, JSON.stringify(saved.slice(-30)));
      } catch (err) { /* storage unavailable: still proceed */ }

      const updates = {};
      drop.forEach(i => { updates[i.key] = 0; });
      fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ updates }),
      }).then(() => { window.location.href = '/checkout'; });
    });
  }

  /* ---- groups: delivery, preorder, saved --------------------------------- */

  const tabs = page.querySelector('[data-cart-tabs]');
  const savedPanel = page.querySelector('[data-saved-panel]');
  const groupEmpty = page.querySelector('[data-group-empty]');
  const itemsSlab = page.querySelector('.cart-items');
  const splitNote = page.querySelector('[data-cart-splitnote]');

  const readSaved = () => {
    try { return JSON.parse(localStorage.getItem(KEY) || '[]'); } catch (err) { return []; }
  };
  const writeSaved = (list) => {
    try { localStorage.setItem(KEY, JSON.stringify(list.slice(-30))); } catch (err) { /* no storage */ }
  };

  function renderSaved() {
    const saved = readSaved();
    const savedTab = page.querySelector('[data-cart-tab="saved"]');
    if (savedTab) savedTab.textContent = savedTab.dataset.tabName + ' (' + saved.length + ')';
    if (!savedPanel) return;
    // Titles come back out of localStorage, so they are built as text nodes
    // rather than markup.
    savedPanel.textContent = '';
    if (!saved.length) {
      const none = document.createElement('p');
      none.className = 'cart-saved__none';
      none.textContent = 'Nothing saved yet. Uncheck a line before checking out and it waits here.';
      savedPanel.append(none);
      return;
    }
    const list = document.createElement('ul');
    list.className = 'cart-saved__list';
    saved.forEach((s, n) => {
      const li = document.createElement('li');
      const name = document.createElement('span');
      name.textContent = s.title || 'Saved item';
      const restore = document.createElement('button');
      restore.type = 'button';
      restore.dataset.restore = String(n);
      restore.textContent = 'Move to bag';
      li.append(name, restore);
      list.append(li);
    });
    const clear = document.createElement('button');
    clear.type = 'button';
    clear.className = 'cart-saved__clear';
    clear.dataset.clearSaved = '';
    clear.textContent = 'Clear list';
    savedPanel.append(list, clear);
  }

  if (savedPanel) {
    savedPanel.addEventListener('click', (e) => {
      const r = e.target.closest('[data-restore]');
      if (r) {
        const saved = readSaved();
        const item = saved[Number(r.dataset.restore)];
        if (!item) return;
        fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: item.id, quantity: item.quantity }] }),
        }).then(() => {
          saved.splice(Number(r.dataset.restore), 1);
          writeSaved(saved);
          window.location.reload();
        });
      }
      if (e.target.closest('[data-clear-saved]')) {
        localStorage.removeItem(KEY);
        renderSaved();
      }
    });
  }

  function showGroup(group) {
    if (!tabs) return;
    tabs.querySelectorAll('[data-cart-tab]').forEach(b => {
      const on = b.dataset.cartTab === group;
      b.classList.toggle('is-active', on);
      b.setAttribute('aria-pressed', on ? 'true' : 'false');
    });

    const saved = group === 'saved';

    let shown = 0;
    page.querySelectorAll('[data-cart-item]').forEach(row => {
      const match = !saved && row.dataset.cartGroup === group;
      row.hidden = !match;
      if (match) shown++;
    });

    /* An empty group hides the slab outright rather than leaving the gift
       ladder and the recommendations standing with a stray line of apology
       underneath them. Same shape as the saved tab: one card, one sentence. */
    const empty = !saved && shown === 0;
    if (savedPanel) savedPanel.hidden = !saved;
    if (itemsSlab) itemsSlab.hidden = saved || empty;
    if (splitNote) splitNote.hidden = saved || empty;

    if (groupEmpty) {
      groupEmpty.hidden = !empty;
      groupEmpty.textContent = group === 'preorder'
        ? 'No preorders in your bag. Anything with a later ship date waits here.'
        : 'Nothing ready to ship right now.';
    }
    try { sessionStorage.setItem('todo:cart-tab', group); } catch (err) { /* no storage */ }
  }

  if (tabs) {
    tabs.addEventListener('click', (e) => {
      const b = e.target.closest('[data-cart-tab]');
      if (b) showGroup(b.dataset.cartTab);
    });
  }

  /* A mixed bag is still one order, because Shopify cannot split a checkout.
     Leave a note on the order so the in-stock half can be fulfilled straight
     away and the preorder line followed up separately. */
  function flagSplitShip() {
    const groups = new Set([...page.querySelectorAll('[data-cart-item]')].map(r => r.dataset.cartGroup));
    const mixed = groups.has('preorder') && groups.has('delivery');
    const current = (cart && cart.attributes && cart.attributes.Fulfilment) || '';
    const wanted = mixed ? 'Split ship: preorder and in stock in one order' : '';
    if (current === wanted) return;
    fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ attributes: { Fulfilment: wanted } }),
    }).catch(() => { /* the note is a convenience, never block checkout */ });
  }

  load().then(() => {
    renderSaved();
    let start = 'delivery';
    try { start = sessionStorage.getItem('todo:cart-tab') || 'delivery'; } catch (err) { /* no storage */ }
    const hasDelivery = page.querySelector('[data-cart-item][data-cart-group="delivery"]');
    if (start === 'delivery' && !hasDelivery && page.querySelector('[data-cart-item][data-cart-group="preorder"]')) {
      start = 'preorder';
    }
    showGroup(start);
    flagSplitShip();
  });
})();
